import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { addSuccess } from '../../actions/SuccessActions';
import { setInspectionMeasurementDataIframeSrc } from '../../actions/WorkflowActions';
import { UNSAVED_CONFIRM_MESSAGE } from '../../constants/Common';
import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getUITA } from '../../utils/commonUtils';
import { getEnvVar } from '../../utils/env';
import ControlChangesIcon from '../components/ControlChangesIcon';
import { FORM_MODEL_ATTRIBUTES } from '../constants/form-manager.constants';
import { FCM_BUTTON_STYLES } from '../constants/runtime.constants';
import { getFormInfo, getWorkflowInfo } from '../selectors/common.selectors';
import { getPropByModel } from '../selectors/form-manager.selectors';
import { getInspectionMeasurementDataQueryParams } from '../selectors/runtime.selectors';
import { getQueryString } from '../utils/controls/ssButtonDecisionTree.utils';

const useStyles = makeStyles({
  button: {
    minWidth: 128,
  },
  ...FCM_BUTTON_STYLES,
});

const SSInspectionMeasurementDataButton = ({
  label,
  buttonType,
  id,
  isEditor,
  isControlHiddenFCM,
  isControlAddedFCM,
  isControlChangedFCM,
}) => {
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const { itmId, stepId, workflowId } = useFormSelector(getWorkflowInfo);
  const { formEntityKey: entityType } = useFormSelector(getFormInfo);
  const classes = useStyles({ isControlAddedFCM });
  const inspectionMeasurementDataQueryParams = useFormSelector(
    getInspectionMeasurementDataQueryParams,
    {
      id,
      entityType,
    }
  );

  const propLabel = useFormSelector(getPropByModel, {
    id,
    prop: FORM_MODEL_ATTRIBUTES.LABEL,
  });

  useEffect(() => () => dispatch(setInspectionMeasurementDataIframeSrc('')), [dispatch]);

  const redirectUrl = useMemo(() => {
    const redirectUrlQueryParams = {
      entityType,
      formKey: `${entityType}_DETAIL`,
      itmId,
      stepId,
      workflowId,
    };

    return `${window.location.pathname}?${getQueryString(redirectUrlQueryParams)}`;
  }, [entityType, itmId, stepId, workflowId]);

  const onClickHandler = () => {
    const inspectionMeasurementDataQuery = getQueryString(inspectionMeasurementDataQueryParams);
    const appUrl = getEnvVar('REACT_APP_ADMIN_APP_URL');
    const pathName = window.location.pathname.replace(/^\/|$/g, '');
    const reloadMessage = translateString('Reloading...');
    const confirmMessage = translateString(UNSAVED_CONFIRM_MESSAGE);
    const inspectionMeasurementDataIframeSrc = `${appUrl}/Pages/MeasurementData.aspx?${inspectionMeasurementDataQuery}&pathName=${pathName}&entityType=${entityType}&formKey=${entityType}_DETAIL&itmId=${itmId}&stepId=${stepId}&workflowId=${workflowId}&reloadMessage=${reloadMessage}&confirmMessage=${confirmMessage}`;

    dispatch(setInspectionMeasurementDataIframeSrc(inspectionMeasurementDataIframeSrc));

    window.addEventListener('message', (event) => {
      if (event.data?.saved) {
        dispatch(addSuccess({ message: translateString('Saved successfully') }));
        window.location = redirectUrl;
      } else if (event.data?.close) {
        dispatch(setInspectionMeasurementDataIframeSrc(''));
      }
    });
  };

  return (
    <span
      className={isControlAddedFCM || isControlChangedFCM ? classes.changesModeWrapper : null}
      ui-ta={getUITA('FormCtrl', 'SSInspectionMeasurementDataButton', buttonType)}
      data-ss-id={id}
    >
      <Button
        color="primary"
        className={classes.button}
        disabled={isEditor || isControlHiddenFCM}
        onClick={onClickHandler}
        variant="secondary"
      >
        {translateString(propLabel ? label : 'Enter Data')}
      </Button>
      {isControlChangedFCM && (
        <span className={classes.changesIcon}>
          <ControlChangesIcon id={id} />
        </span>
      )}
    </span>
  );
};
export default SSInspectionMeasurementDataButton;
